import React, { useCallback, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, AllCommunityModule } from 'ag-grid-community';
import { ModuleRegistry, themeBalham } from 'ag-grid-community';
import { RapportCarte } from '../../../utils/types/RapportCarte';
import * as XLSX from 'xlsx';

ModuleRegistry.registerModules([AllCommunityModule]);

interface RapportCarteGridProps {
    reportItems: RapportCarte[];
}

const RapportCarteGrid: React.FC<RapportCarteGridProps> = ({ reportItems }) => {
    const gridRef = useRef<AgGridReact>(null);

    const [columnDefs] = useState<ColDef<RapportCarte>[]>([
        { field: 'numeroCarte', headerName: 'Numéro Carte', sortable: true, filter: true },
        { field: 'typeCarte', headerName: 'Type carte', sortable: true, filter: true },
        { field: 'porteurCarte', headerName: 'Porteur', sortable: true, filter: true },
        {
            field: 'totalRechargeQteGO',
            headerName: 'Recharge Qte GO',
            sortable: true,
            filter: 'agNumberColumnFilter',
            valueFormatter: (params) => (params.value != null ? `${params.value.toFixed(2).replace('.', ',')} L` : ''),
            cellStyle: { textAlign: 'right' },
        },
        {
            field: 'totalRechargeQteES',
            headerName: 'Recharge Qte ES',
            sortable: true,
            filter: 'agNumberColumnFilter',
            valueFormatter: (params) => (params.value != null ? `${params.value.toFixed(2).replace('.', ',')} L` : ''),
            cellStyle: { textAlign: 'right' },
        },
        {
            field: 'totalRechargeValeur',
            headerName: 'Recharge Valeur',
            sortable: true,
            filter: 'agNumberColumnFilter',
            valueFormatter: (params) => (params.value != null ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value) : ''),
            cellStyle: { textAlign: 'right' },
        },
        {
            field: 'totalPriseQteGO',
            headerName: 'Prise Qte GO',
            sortable: true,
            filter: 'agNumberColumnFilter',
            valueFormatter: (params) => (params.value != null ? `${params.value.toFixed(2).replace('.', ',')} L` : ''),
            cellStyle: { textAlign: 'right' },
        },
        {
            field: 'totalPriseQteES',
            headerName: 'Prise Qte ES',
            sortable: true,
            filter: 'agNumberColumnFilter',
            valueFormatter: (params) => (params.value != null ? `${params.value.toFixed(2).replace('.', ',')} L` : ''),
            cellStyle: { textAlign: 'right' },
        },
        {
            field: 'totalPriseValeur',
            headerName: 'Prise Valeur',
            sortable: true,
            filter: 'agNumberColumnFilter',
            valueFormatter: (params) => (params.value != null ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value) : ''),
            cellStyle: { textAlign: 'right' },
        },
    ]);

    const myTheme = themeBalham.withParams({ columnBorder: { style: 'dashed', width: '1px' } });

    const pinnedBottomRowData = [
        {
            numeroCarte: 'Totaux',
            totalRechargeQteGO: reportItems.reduce((sum, i) => sum + (i.totalRechargeQteGO ?? 0), 0),
            totalRechargeQteES: reportItems.reduce((sum, i) => sum + (i.totalRechargeQteES ?? 0), 0),
            totalRechargeValeur: reportItems.reduce((sum, i) => sum + (i.totalRechargeValeur ?? 0), 0),
            totalPriseQteGO: reportItems.reduce((sum, i) => sum + (i.totalPriseQteGO ?? 0), 0),
            totalPriseQteES: reportItems.reduce((sum, i) => sum + (i.totalPriseQteES ?? 0), 0),
            totalPriseValeur: reportItems.reduce((sum, i) => sum + (i.totalPriseValeur ?? 0), 0),
        },
    ];

    const exportAsCsv = useCallback(() => {
        if (!gridRef.current) return;
        const cols = gridRef.current.api.getColumns()?.filter((c) => !c.getColDef().hide);
        if (!cols?.length) {
            alert('Aucune colonne visible à exporter');
            return;
        }
        const keys = cols.map((c) => c.getColId());
        const ts = new Date().toISOString().replace(/[:.]/g, '-');
        gridRef.current.api.exportDataAsCsv({ fileName: `rapport_cartes_${ts}.csv`, allColumns: false, columnKeys: keys, suppressQuotes: false, skipPinnedBottom: true });
    }, []);

    const exportAsExcel = useCallback(() => {
        if (!gridRef.current) return;
        const cols = gridRef.current.api.getColumns()?.filter((c) => !c.getColDef().hide);
        if (!cols?.length) {
            alert('Aucune colonne visible à exporter');
            return;
        }
        const data: RapportCarte[] = [];
        gridRef.current.api.forEachNodeAfterFilterAndSort((n) => data.push(n.data));
        if (!data.length) {
            alert('Aucune donnée à exporter');
            return;
        }
        const exportData = data.map((row) => {
            const obj: any = {};
            cols.forEach((c) => {
                const def = c.getColDef();
                const f = def.field;
                const h = def.headerName ?? f ?? '';
                if (!f) return;
                let v = (row as any)[f];
                if (['totalRechargeQteGO', 'totalRechargeQteES', 'totalPriseQteGO', 'totalPriseQteES'].includes(f)) {
                    v = v != null ? v.toFixed(2) : '';
                }
                if (['totalRechargeValeur', 'totalPriseValeur'].includes(f)) {
                    v = v != null ? v.toFixed(2) : '';
                }
                obj[h] = v;
            });
            return obj;
        });
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Rapport');
        const ts = new Date().toISOString().replace(/[:.]/g, '-');
        XLSX.writeFile(wb, `rapport_cartes_${ts}.xlsx`);
    }, []);

    return (
        <>
            <div style={{ height: 500, width: '100%' }}>
                <AgGridReact ref={gridRef} theme={myTheme} getRowId={(params) => params.data.idCarte?.toString() ?? ''} rowData={reportItems} columnDefs={columnDefs} pagination domLayout="normal" defaultColDef={{ resizable: true, editable: false, minWidth: 100, flex: 1 }} suppressExcelExport enableCellTextSelection ensureDomOrder animateRows pinnedBottomRowData={pinnedBottomRowData} overlayNoRowsTemplate="<span style='padding: 10px;'>Aucune donnée ...</span>" />
            </div>

            <div className="dropdown mt-1">
                <button className="btn dropdown-toggle" type="button" id="exportDropdown" data-bs-toggle="dropdown">
                    Export
                </button>
                <ul className="dropdown-menu" aria-labelledby="exportDropdown">
                    <li>
                        <button className="dropdown-item" onClick={exportAsCsv}>
                            Export as CSV
                        </button>
                    </li>
                    <li>
                        <button className="dropdown-item" onClick={exportAsExcel}>
                            Export as Excel
                        </button>
                    </li>
                </ul>
            </div>
        </>
    );
};

export default RapportCarteGrid;
